import { useEffect, useMemo, useRef, useState } from 'react';

function stableStringify(obj: any): string {
  return JSON.stringify(obj, function replacer(key, value) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return Object.keys(value)
        .sort()
        .reduce((acc: any, k) => {
          acc[k] = (value as any)[k];
          return acc;
        }, {});
    }
    return value;
  });
}

export function useDebounce<T, S>(
  value: T,
  selector: (v: T) => S,
  delay: number,
): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  const selected = useMemo(() => selector(value), [value, selector]);
  const selectedKey = useMemo(() => stableStringify(selected), [selected]);

  const latestValueRef = useRef(value);
  useEffect(() => {
    latestValueRef.current = value;
  }, [value]);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedValue(latestValueRef.current);
    }, delay);
    return () => clearTimeout(id);
  }, [selectedKey, delay]);

  return debouncedValue;
}
